import Cookies from "js-cookie";
import { Constants, SdkConfig } from "../globals";
import { UserAgentUtils } from "../utils";

export default class Campaign {
  /**
   * @param id  캠페인 아이디 | A/B Test 고유 아이디
   * @param templateType A | B | C
   * @param isPreview 미리보기 여부
   * @param variantKey A/B 테스트 시안 구분
   * @param triggerYn 트리거 여부
   * @param $reference CAMPAIGN | AB_TEST
   * @param onConversion 클릭 시 이벤트 핸들러
   * @param templates
   * @param triggerRules
   * @param msgTemplateObject
   */
  constructor({ id, msgTemplateObject, triggerRules, onConversion, isPreview, variantKey, triggerYn, templates, templateType, $reference }) {
    this.popupId = `cdp-popup-${isPreview ? 'preview-' : ''}${id}`
    this.id = id
    this.triggerRules = triggerRules
    this.onConversion = onConversion
    this.isPreview = isPreview
    this.$reference = $reference
    this.triggerYn = triggerYn
    this.templateType = templateType
    if (isPreview && $reference === 'AB_TEST') {
      this.msgTemplateObject = {
        ...templates[Number(variantKey) - 1]?.msgTemplateObject,
        templateType
      }
    } else {
      this.msgTemplateObject = msgTemplateObject
    }
  }

  handleConversion(onConversion) {
    this.onConversion = onConversion
  }

  isTodayClose() {
    const cookie = Cookies.get(Constants.COOKIE_NAME_CAMPAIGN_TODAY_NONE_EXPOSURE_LIST)
    const todayHideCampaignIdList = cookie?.split('|') || []
    return todayHideCampaignIdList.includes(this.popupId)
  }

  /**
   * @param validationContext {{ referrer?: string }}
   */
  getTriggerRuleAsEventList(validationContext = {}) {
    const isMatchOperator = (operator, origin, value) => {
      if (operator === 'Contains') {
        return origin?.includes(value)
      } else if (operator === 'Equals') {
        return origin === value
      } else {
        return false
      }
    }
    const isMatch = (key, operator, url, value) => {
      if (key === 'pageUrl') {
        return isMatchOperator(operator, url.href, value)
      } else {
        return isMatchOperator(operator, url.searchParams.get(key), value)
      }
    }
    return this.triggerRules.filter(({ division, eventId, value, operator, key }) => {
      if (eventId === 'page_view') {
        return isMatch(key, operator, new URL(division === 'current' ? window.location.href : validationContext.referrer), value)
      }
      return true
    }).map(({ division, eventId, value, operator, key, triggerMode, timeOnPage }) => {
      if (eventId === 'page_view') {
        return {
          timeOnPage,
          triggerMode,
        }
      }
      if (eventId === 'button_click') {
        return {
          timeOnPage,
          triggerMode,
          isDetectedEvent: true,
          targetElements: document.querySelectorAll(`*[data-cdp-ui-key="${value}"]`),
          selector: `*[data-cdp-ui-key="${value}"]`,
          action: 'click'
        }
      }
    })
  }

  getConnectionUrlWithParams() {
    try {
      if (this.msgTemplateObject?.connectionUrl) {
        const url = new URL(this.msgTemplateObject.connectionUrl)
        url.searchParams.append('utmCampaignId', this.id)
        if (this.$reference === 'AB_TEST' && this.msgTemplateObject.sortOrder) {
          url.searchParams.append('utmVariantKey', this.msgTemplateObject.sortOrder)
        }
        return url.toString()
      }
    } catch (error) {
      if (SdkConfig.isDebugMode()) {
        console.error(error)
      }
    }
    return `javascript:;`
  }

  getPopup() {
    const popup = {
      'A': {
        html: this.modalTemplate(),
        targetElement: document.body,
        targetElementPosition: 'beforeend',
      },
      'B': {
        html: this.bannerTemplate(),
        targetElement: document.body,
        targetElementPosition: 'beforeend',
      },
      'C': {
        html: this.topBannerTemplate(),
        targetElement: document.querySelector('header') || document.body,
        targetElementPosition: 'beforebegin',
      },
    }
    return {
      ...popup[this.msgTemplateObject.templateType],
      id: this.popupId,
      onConversion: this.onConversion,
      templateType: this.msgTemplateObject.templateType
    }
  }

  getPreviewPopup() {
    if (this.$reference === 'AB_TEST') {
      const popup = {
        'A': {
          html: this.modalTemplate(),
          targetElement: document.body,
          targetElementPosition: 'beforeend',
        },
        'B': {
          html: this.bannerTemplate(),
          targetElement: document.body,
          targetElementPosition: 'beforeend',
        },
        'C': {
          html: this.topBannerTemplate(),
          targetElement: document.querySelector('header') || document.body,
          targetElementPosition: 'beforebegin',
        },
      }
      return {
        id: this.popupId,
        onConversion: this.onConversion,
        templateType: this.msgTemplateObject.templateType,
        ...popup[this.msgTemplateObject.templateType]
      }
    }
    return this.getPopup()
  }

  modalTemplate() {
    const { buttonName, buttonUrl, buttonColor } = (() => ({
      buttonName: this.msgTemplateObject.buttonName || '',
      buttonUrl: this.msgTemplateObject.buttonUrl || '',
      buttonColor: this.msgTemplateObject.buttonColor || 'black',
    }))()
    return `
      <div id="${this.popupId}" class="ui-cdp-modal feedbackSystemPop" style="display: none;">
        <div class="layerpop ui-cdp-modal-container">
          <div class="feedback_system">
            <a href="javascript:;" data-cdp-core-element="conversion">
              <img src="${this.msgTemplateObject.imageUrl}" alt="${this.msgTemplateObject.imageDescription}"/>
              ${buttonUrl?.length > 0 ? `
                <a href="${buttonUrl}" class="btn_join" data-cdp-core-element="conversion" style="background-color: ${buttonColor}">${buttonName}</a>
              ` : ``}
            </a>
            <div class="btn">
              ${this.$reference !== 'AB_TEST' ? `
                <button type="button" class="ui-cdp-popup-close-today-button">오늘 하루 안 보기</button>
              ` : ``}
              <button type="button" class="close ui-cdp-popup-close-button">닫기</button>
            </div>
          </div>
        </div>
        <div class="ui-cdp-dimmed"></div>
      </div>
    `
  }

  bannerTemplate() {
    const { buttonUrl } = (() => ({
      buttonUrl: this.msgTemplateObject.buttonUrl || '',
    }))()
    const isMobile = UserAgentUtils.isMobileDeviceWidth()
    return `
      <div id="${this.popupId}" class="feedback_system_banner" style="display: none; ${!isMobile ? `background: ${this.msgTemplateObject.pcColor};` : ''}">
        <div class="inr" style="${isMobile ? `background: ${this.msgTemplateObject.mobileColor}` : ''}">
          <img src="${this.msgTemplateObject.imageUrl}" class="pc" alt="${this.msgTemplateObject.imageDescription}">
          <img src="${this.msgTemplateObject.imageMobileUrl}" class="mo" alt="${this.msgTemplateObject.imageDescription}">
          <a class="ui-cdp-link" href="javascript:;" data-cdp-core-element="conversion">확인하러 가기</a>
        </div>
        <div class="btn">
          ${this.$reference !== 'AB_TEST' ? `
            <button type="button" class="ui-cdp-popup-close-today-button">오늘 하루 안 보기</button>
          ` : ``}
          <button type="button" class="close ui-cdp-popup-close-button">닫기</button>
        </div>
      </div>
    `
  }

  topBannerTemplate() {
    const { buttonUrl } = (() => ({
      buttonUrl: this.msgTemplateObject.buttonUrl || '',
    }))()
    const isMobile = UserAgentUtils.isMobileDeviceWidth()
    return `
      <div id="${this.popupId}" class="feedback_system_top_banner" style="display: none; ${!isMobile ? `background: ${this.msgTemplateObject.pcColor};` : ''}">
        <div class="inr" style="${isMobile ? `background: ${this.msgTemplateObject.mobileColor}` : ''}">
          <img src="${this.msgTemplateObject.imageUrl}" class="pc" alt="${this.msgTemplateObject.imageDescription}">
          <img src="${this.msgTemplateObject.imageMobileUrl}" class="mo" alt="${this.msgTemplateObject.imageDescription}">
          <a class="ui-cdp-link" href="javascript:;" data-cdp-core-element="conversion">확인하러 가기</a>
        </div>
        <div class="btn">
          ${this.$reference !== 'AB_TEST' ? `
            <button type="button" class="ui-cdp-popup-close-today-button">오늘 하루 안 보기</button>
          ` : ``}
          <button type="button" class="close ui-cdp-popup-close-button">닫기</button>
        </div>
      </div>
    `
  }
}
